import React, { useState, useEffect } from 'react';
import type { Repository } from '@cowork-gen/core';
import { api } from '../api/client';

export function Settings() {
  const [repos, setRepos] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(true);
  const [newRepoUrl, setNewRepoUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadRepos();
  }, []);

  async function loadRepos() {
    try {
      const response = await api.getRepos();
      if (response.success && response.data) {
        setRepos(response.data);
      }
    } catch (err) {
      console.error('Failed to load repos:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddRepo(e: React.FormEvent) {
    e.preventDefault();
    if (!newRepoUrl.trim()) return;

    setAdding(true);
    try {
      await api.createRepo({
        githubUrl: newRepoUrl.trim(),
        webhookSecret: webhookSecret.trim() || undefined,
      });
      setNewRepoUrl('');
      setWebhookSecret('');
      await loadRepos();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add repository');
    } finally {
      setAdding(false);
    }
  }

  async function handleDeleteRepo(repo: Repository) {
    if (!confirm(`Delete repository ${repo.id}?`)) return;

    try {
      await api.deleteRepo(repo.id);
      await loadRepos();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete repository');
    }
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
      </div>

      <div className="p-4">
        <h3 className="text-md font-medium text-gray-900 mb-4">Repositories</h3>

        {/* Add Repository Form */}
        <form onSubmit={handleAddRepo} className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GitHub Repository URL
              </label>
              <input
                type="text"
                value={newRepoUrl}
                onChange={(e) => setNewRepoUrl(e.target.value)}
                placeholder="https://github.com/owner/repo"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook Secret (optional)
              </label>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="Your webhook secret"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={adding || !newRepoUrl.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {adding ? 'Adding...' : 'Add Repository'}
            </button>
          </div>
        </form>

        {/* Repository List */}
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        ) : repos.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            No repositories configured
          </p>
        ) : (
          <div className="divide-y divide-gray-200">
            {repos.map((repo) => (
              <div key={repo.id} className="py-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{repo.id}</p>
                  <p className="text-sm text-gray-500">{repo.githubUrl}</p>
                  <p className="text-xs text-gray-400">
                    Default branch: {repo.defaultBranch}
                    {repo.webhookSecret && ' • Webhook configured'}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteRepo(repo)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Webhook Setup Instructions */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">
            GitHub Webhook Setup
          </h4>
          <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
            <li>Go to your repository's Settings → Webhooks → Add webhook</li>
            <li>
              Set Payload URL to:{' '}
              <code className="bg-blue-100 px-1 rounded">
                {window.location.origin}/webhook/github
              </code>
            </li>
            <li>Set Content type to: application/json</li>
            <li>Set a Secret and save it in the field above</li>
            <li>Select events: Issues, Issue comments, Pull requests, Pushes</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
